export const CPC_MODELS = Object.freeze({
  square: { printWidth: 55, printHeight: 55, patchWidth: 70, patchHeight: 70, frame: [0.396, 0.292, 0.228, 0.221] },
  black: { printWidth: 70, printHeight: 10, patchWidth: 95, patchHeight: 30, frame: [0.397, 0.429, 0.206, 0.024], aspectRatio: 0.75, textColor: "#ffffff" },
  rectangle: { printWidth: 70, printHeight: 10, patchWidth: 95, patchHeight: 30, frame: [0.374, 0.411, 0.25, 0.036] }
});

export const CPC_FONTS = Object.freeze({
  sans: 'Arial, Helvetica, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: '"Courier New", Courier, monospace'
});

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
export const effectiveTextColor = (model, state) => model.textColor || (/^#[a-f\d]{6}$/i.test(state.color) ? state.color : '#151515');
const mmToPixels = mm => Math.round(mm / 25.4 * 300);

export function fitInside(areaWidth, areaHeight, sourceWidth, sourceHeight, scale = 100, x = 50, y = 50) {
  if (![areaWidth, areaHeight, sourceWidth, sourceHeight].every(value => Number.isFinite(value) && value > 0)) return null;
  const ratio = Math.min(areaWidth / sourceWidth, areaHeight / sourceHeight) * clamp(scale, 5, 100) / 100;
  const width = Math.min(areaWidth, sourceWidth * ratio);
  const height = Math.min(areaHeight, sourceHeight * ratio);
  return { width, height, x: Math.max(0, areaWidth - width) * clamp(x, 0, 100) / 100, y: Math.max(0, areaHeight - height) * clamp(y, 0, 100) / 100 };
}

export function measureTextDesign(context, state) {
  const lines = String(state.text || '').replace(/\r/g, '').split('\n').slice(0, 4);
  context.font = `${state.weight === '400' ? '400' : '700'} 300px ${CPC_FONTS[state.font] || CPC_FONTS.sans}`;
  const measurements = lines.map(line => context.measureText(line || ' '));
  const ascent = Math.max(1, ...measurements.map(item => item.actualBoundingBoxAscent || 220));
  const descent = Math.max(1, ...measurements.map(item => item.actualBoundingBoxDescent || 55));
  const width = Math.max(1, ...measurements.map(item => Math.max(item.width, (item.actualBoundingBoxLeft || 0) + (item.actualBoundingBoxRight || 0))));
  return { width, height: ascent + descent + (lines.length - 1) * 345, lines, measurements, ascent, lineHeight: 345 };
}

export function drawPrint(canvas, model, state, sourceImage = null) {
  canvas.width = mmToPixels(model.printWidth);
  canvas.height = mmToPixels(model.printHeight);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas');
  const text = state.mode === 'text' && String(state.text || '').trim();
  const hasImage = state.mode === 'image' && sourceImage;
  if (!text && !hasImage) return null;
  const source = hasImage
    ? { width: sourceImage.naturalWidth || sourceImage.width, height: sourceImage.naturalHeight || sourceImage.height }
    : measureTextDesign(context, state);
  const box = fitInside(model.printWidth, model.printHeight, source.width, source.height, state.scale, state.x, state.y);
  if (!box) return null;
  context.save();
  context.translate(box.x / model.printWidth * canvas.width, box.y / model.printHeight * canvas.height);
  context.scale(box.width / source.width / model.printWidth * canvas.width, box.height / source.height / model.printHeight * canvas.height);
  if (hasImage) {
    context.drawImage(sourceImage, 0, 0);
  } else {
    context.fillStyle = effectiveTextColor(model, state);
    context.textBaseline = 'alphabetic';
    source.lines.forEach((line, index) => {
      const measurement = source.measurements[index];
      const left = measurement.actualBoundingBoxLeft || 0;
      const inkWidth = left + (measurement.actualBoundingBoxRight || measurement.width);
      context.fillText(line, (source.width - inkWidth) / 2 + left, source.ascent + index * source.lineHeight);
    });
  }
  context.restore();
  return box;
}

function crc32(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export async function pngAt300Dpi(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (bytes.length < 33 || bytes[0] !== 137 || bytes[1] !== 80 || bytes[2] !== 78 || bytes[3] !== 71) throw new Error('png');
  const parts = [bytes.slice(0, 33)];
  const chunk = new Uint8Array(21);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, 9);
  chunk.set([112, 72, 89, 115], 4);
  view.setUint32(8, 11811);
  view.setUint32(12, 11811);
  chunk[16] = 1;
  view.setUint32(17, crc32(chunk.slice(4, 17)));
  parts.push(chunk);
  let offset = 33;
  while (offset < bytes.length) {
    if (offset + 12 > bytes.length) throw new Error('png');
    const length = new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
    const end = offset + length + 12;
    if (end > bytes.length) throw new Error('png');
    const type = String.fromCharCode(...bytes.slice(offset + 4, offset + 8));
    if (type !== 'pHYs') parts.push(bytes.slice(offset, end));
    offset = end;
  }
  return new Blob(parts, { type: 'image/png' });
}

export function designMetadata(modelKey, state, box, originalName = '') {
  const model = CPC_MODELS[modelKey];
  if (!model || !box) throw new Error('design');
  const metadata = {
    version: 1, model: modelKey, mode: state.mode,
    printAreaMm: { width: model.printWidth, height: model.printHeight },
    designMm: Object.fromEntries(Object.entries(box).map(([key, value]) => [key, Math.round(value * 1000) / 1000])),
    output: { format: 'PNG', dpi: 300, width: mmToPixels(model.printWidth), height: mmToPixels(model.printHeight) }
  };
  if (state.mode === 'text') Object.assign(metadata, { text: state.text, font: CPC_FONTS[state.font] || CPC_FONTS.sans, weight: state.weight, color: effectiveTextColor(model, state) });
  if (state.mode === 'image') metadata.originalName = originalName;
  return metadata;
}

class CpcPersonalizer extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.config = JSON.parse(this.querySelector('[data-cpc-config]').textContent);
    this.labels = this.config.labels;
    this.state = { model: this.config.initialModel || 'square', mode: 'text', text: '', font: 'sans', weight: '700', color: '#151515', scale: 85, x: 50, y: 50 };
    this.canvas = this.querySelector('[data-cpc-canvas]');
    this.form = this.querySelector('form');
    this.view = 'front';
    this.sandColor = '#151515';
    this.image = null;
    this.imageFile = null;
    this.imageRequest = 0;
    this.version = 0;
    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    const followShapeLink = () => {
      const model = location.hash.replace('#cap-gestalten-', '');
      if (Object.hasOwn(CPC_MODELS, model)) { this.state.model = model; this.update(); }
    };
    window.addEventListener('hashchange', followShapeLink, options);
    document.addEventListener('click', event => {
      const href = event.target.closest('a')?.getAttribute('href');
      const linkedModel = href?.replace('#cap-gestalten-', '');
      if (href?.startsWith('#cap-gestalten-') && Object.hasOwn(CPC_MODELS, linkedModel)) {
        this.state.model = linkedModel;
        this.update();
      }
    }, options);
    for (const button of this.querySelectorAll('[data-cpc-view]')) button.addEventListener('click', () => { this.view = button.dataset.cpcView; this.update(); }, options);
    this.form.addEventListener('input', event => this.onInput(event), options);
    this.form.addEventListener('change', event => this.onInput(event), options);
    this.form.addEventListener('submit', event => this.submit(event), options);
    this.querySelector('[data-cpc-center]').addEventListener('click', () => { this.state.x = 50; this.state.y = 50; this.update(); }, options);
    this.querySelector('[data-cpc-download]').addEventListener('click', () => this.download(), options);
    this.canvas.addEventListener('pointerdown', event => this.startDrag(event), options);
    this.canvas.addEventListener('pointermove', event => this.moveDrag(event), options);
    this.canvas.addEventListener('pointerup', () => { this.drag = null; }, options);
    this.canvas.addEventListener('pointercancel', () => { this.drag = null; }, options);
    this.canvas.addEventListener('keydown', event => this.onKey(event), options);
    this.querySelector('[data-cpc-photo]').addEventListener('load', () => { this.querySelector('[data-cpc-photo-error]').hidden = true; }, options);
    this.querySelector('[data-cpc-photo]').addEventListener('error', () => { this.querySelector('[data-cpc-photo-error]').hidden = false; }, options);
    this.querySelector('[data-cpc-controls]').disabled = false;
    this.querySelector('[data-cpc-no-js]').hidden = true;
    this.classList.add('cpc-ready');
    followShapeLink();
    this.update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageRequest = (this.imageRequest || 0) + 1;
    this.initialized = false;
  }

  field(name) { return this.querySelector(`[data-cpc-field="${name}"]`); }
  notice(message = '') { this.querySelector('[data-cpc-error]').textContent = message; }

  onInput(event) {
    const target = event.target;
    const key = target.dataset.cpcField;
    if (!key) return;
    if (key === 'image') {
      if (event.type === 'change') this.loadImage(target.files?.[0]);
      return;
    }
    if (target.type === 'radio' && !target.checked) return;
    if (key === 'guide') { this.querySelector('[data-cpc-stage]').classList.toggle('cpc-guide-hidden', !target.checked); return; }
    if (key === 'color' && !CPC_MODELS[this.state.model].textColor) this.sandColor = target.value;
    if (key === 'text') {
      target.value = target.value.replace(/\r/g, '').split('\n').slice(0, 4).join('\n').slice(0, 120);
    }
    if (Object.hasOwn(this.state, key)) this.state[key] = ['scale', 'x', 'y'].includes(key) ? Number(target.value) : target.value;
    this.notice();
    this.update();
  }

  async loadImage(file) {
    const request = ++this.imageRequest;
    this.image = null;
    this.imageFile = null;
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageUrl = null;
    if (!file) { this.update(); return; }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      this.field('image').value = '';
      this.notice(this.labels.imageError);
      this.update();
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    try {
      image.src = url;
      await image.decode();
      if (request !== this.imageRequest || !this.isConnected) { URL.revokeObjectURL(url); return; }
      if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 25000000) throw new Error('dimensions');
      this.image = image;
      this.imageFile = file;
      this.imageUrl = url;
      this.state.scale = 85;
      this.state.x = 50;
      this.state.y = 50;
      this.notice();
    } catch {
      URL.revokeObjectURL(url);
      if (request !== this.imageRequest) return;
      this.field('image').value = '';
      this.notice(this.labels.imageError);
    }
    this.update();
  }

  update() {
    this.version++;
    const model = CPC_MODELS[this.state.model];
    const item = this.config.products[this.state.model];
    if (!model || !item) return;
    if (this.lastModel !== this.state.model) { this.view = 'front'; this.drag = null; }
    this.state.color = model.textColor || this.sandColor;
    this.lastModel = this.state.model;
    this.field('color').value = this.state.color;
    this.field('color').disabled = Boolean(model.textColor);
    this.querySelector('[data-cpc-white-only]').hidden = !model.textColor;
    const isBack = this.view === 'back' && Boolean(item.backImage);
    this.canvas.hidden = isBack;
    this.querySelector('[data-cpc-stage]').style.aspectRatio = String(model.aspectRatio || 1);
    for (const button of this.querySelectorAll('[data-cpc-view]')) {
      button.setAttribute('aria-pressed', String(button.dataset.cpcView === (isBack ? 'back' : 'front')));
      if (button.dataset.cpcView === 'back') button.disabled = !item.backImage;
    }
    this.querySelector('[data-cpc-back-note]').hidden = !isBack;
    this.querySelector('.cpc-personalizer__guide').hidden = isBack;
    this.querySelector('[id^="cpc-drag-"]').hidden = isBack;
    for (const input of this.querySelectorAll('[data-cpc-field="model"], [data-cpc-field="mode"]')) input.checked = input.value === this.state[input.dataset.cpcField];
    for (const key of ['scale', 'x', 'y']) this.field(key).value = this.state[key];
    this.querySelector('[data-cpc-text-panel]').hidden = this.state.mode !== 'text';
    this.querySelector('[data-cpc-image-panel]').hidden = this.state.mode !== 'image';
    this.field('image').disabled = this.state.mode !== 'image';
    const photo = this.querySelector('[data-cpc-photo]');
    const photoUrl = isBack ? item.backImage : item.image;
    if (photo.getAttribute('src') !== photoUrl) {
      photo.src = photoUrl;
      photo.alt = `${item.title} – ${isBack ? this.labels.back : this.labels.front}`;
    }
    const [left, top, width, height] = model.frame;
    this.canvas.style.left = `${left * 100}%`;
    this.canvas.style.top = `${top * 100}%`;
    this.canvas.style.width = `${width * 100}%`;
    this.canvas.style.height = `${height * 100}%`;
    this.box = drawPrint(this.canvas, model, this.state, this.image);
    const centimeters = mm => (mm / 10).toLocaleString(this.config.locale || 'de-DE', { maximumFractionDigits: 2 });
    this.querySelector('[data-cpc-limit]').textContent = `${centimeters(model.printWidth)} × ${centimeters(model.printHeight)} cm`;
    this.querySelector('[data-cpc-size]').textContent = this.box ? `${centimeters(this.box.width)} × ${centimeters(this.box.height)} cm` : '—';
    this.querySelector('[data-cpc-scale-value]').textContent = `${this.state.scale} %`;
    this.querySelector('[data-cpc-x-value]').textContent = `${Math.round(this.state.x)} %`;
    this.querySelector('[data-cpc-y-value]').textContent = `${Math.round(this.state.y)} %`;
    this.querySelector('[data-cpc-selected-name]').textContent = item.title;
    this.querySelector('[data-cpc-variant]').value = item.variantId || '';
    this.querySelector('[data-cpc-download]').disabled = !this.box;
    const purchasable = this.config.enablePurchase && item.variantId && item.available && item.price > 0;
    this.canPurchase = Boolean(purchasable);
    this.querySelector('[data-cpc-submit]').disabled = !this.canPurchase || !this.box;
    this.querySelector('[data-cpc-submit]').textContent = this.canPurchase ? this.labels.addToCart : this.labels.previewOnly;
    this.querySelector('[data-cpc-price]').textContent = this.canPurchase ? item.priceLabel : this.labels.pricesSoon;
    this.querySelector('[data-cpc-quantity-wrap]').hidden = !this.canPurchase;
    const quality = this.querySelector('[data-cpc-quality]');
    const dpi = this.image && this.box && this.state.mode === 'image' ? Math.min(this.image.naturalWidth / (this.box.width / 25.4), this.image.naturalHeight / (this.box.height / 25.4)) : Infinity;
    quality.textContent = dpi < 150 ? this.labels.lowResolution : '';
  }

  startDrag(event) {
    if (!this.box || this.view === 'back' || event.button !== 0) return;
    event.preventDefault();
    this.canvas.setPointerCapture(event.pointerId);
    this.drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, startX: this.state.x, startY: this.state.y };
  }

  moveDrag(event) {
    if (!this.drag || this.drag.pointerId !== event.pointerId || !this.box) return;
    const model = CPC_MODELS[this.state.model];
    const rect = this.canvas.getBoundingClientRect();
    const freeX = 1 - this.box.width / model.printWidth;
    const freeY = 1 - this.box.height / model.printHeight;
    this.state.x = freeX > 0.00001 ? clamp(this.drag.startX + (event.clientX - this.drag.x) / rect.width / freeX * 100, 0, 100) : 50;
    this.state.y = freeY > 0.00001 ? clamp(this.drag.startY + (event.clientY - this.drag.y) / rect.height / freeY * 100, 0, 100) : 50;
    this.update();
  }

  onKey(event) {
    const keys = { ArrowLeft: ['x', -2], ArrowRight: ['x', 2], ArrowUp: ['y', -2], ArrowDown: ['y', 2] };
    if (!keys[event.key]) return;
    event.preventDefault();
    const [key, direction] = keys[event.key];
    this.state[key] = clamp(this.state[key] + direction, 0, 100);
    this.update();
  }

  async printBlob() {
    const canvas = document.createElement('canvas');
    const box = drawPrint(canvas, CPC_MODELS[this.state.model], this.state, this.image);
    if (!box) throw new Error('empty');
    const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('png')), 'image/png'));
    return pngAt300Dpi(blob);
  }

  async download() {
    const button = this.querySelector('[data-cpc-download]');
    button.disabled = true;
    try {
      const model = CPC_MODELS[this.state.model];
      const blob = await this.printBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `custom-patch-cap-${this.state.model}-${model.printWidth}x${model.printHeight}mm-300dpi.png`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { this.notice(this.labels.exportError); }
    button.disabled = !this.box;
  }

  async submit(event) {
    event.preventDefault();
    if (this.submitting || !this.canPurchase || !this.box || !this.form.reportValidity()) return;
    this.submitting = true;
    const version = this.version;
    this.form.setAttribute('aria-busy', 'true');
    this.querySelector('[data-cpc-submit]').disabled = true;
    try {
      const blob = await this.printBlob();
      if (version !== this.version) throw new Error('changed');
      const model = CPC_MODELS[this.state.model];
      const transfer = new DataTransfer();
      transfer.items.add(new File([blob], `patch-${model.printWidth}x${model.printHeight}mm-300dpi.png`, { type: 'image/png' }));
      const output = this.querySelector('[data-cpc-print-file]');
      output.files = transfer.files;
      output.disabled = false;
      if (output.files.length !== 1) throw new Error('file');
      const metadata = designMetadata(this.state.model, this.state, this.box, this.imageFile?.name || '');
      const cm = mm => (mm / 10).toLocaleString(this.config.locale || 'de-DE', { maximumFractionDigits: 2 });
      const properties = {
        Gestaltung: this.state.mode === 'text' ? this.labels.text : this.labels.image,
        Patchform: this.config.products[this.state.model].shape,
        Druckfläche: `${cm(model.printWidth)} × ${cm(model.printHeight)} cm`,
        Motivgröße: `${cm(this.box.width)} × ${cm(this.box.height)} cm`,
        _CPC_Design: JSON.stringify(metadata),
        _CPC_ID: crypto.randomUUID()
      };
      if (this.state.mode === 'text') Object.assign(properties, { Text: this.state.text, Schriftart: this.state.font, Schriftstärke: this.state.weight, Textfarbe: effectiveTextColor(model, this.state) });
      const container = this.querySelector('[data-cpc-properties]');
      container.replaceChildren();
      for (const [name, value] of Object.entries(properties)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.value = value;
        container.append(input);
      }
      HTMLFormElement.prototype.submit.call(this.form);
    } catch {
      this.notice(this.labels.orderError);
      this.form.removeAttribute('aria-busy');
      this.submitting = false;
      this.querySelector('[data-cpc-submit]').disabled = !this.canPurchase || !this.box;
    }
  }
}

if (globalThis.customElements && !customElements.get('cpc-personalizer')) customElements.define('cpc-personalizer', CpcPersonalizer);

